// ─── panels.js ───────────────────────────────────────────────
// Manages all DOM panel interactions for the Hive Dashboard.
// Pure DOM manipulation — no Three.js or Phaser imports.

// ─── Agent Metadata ──────────────────────────────────────────
const AGENT_META = {
  atlas: { label: 'Atlas', color: '#ffd700', role: 'Strategy & Coordination' },
  muse:  { label: 'Muse',  color: '#ff69b4', role: 'Content Creation' },
  pixel: { label: 'Pixel', color: '#00bfff', role: 'Image Generation' },
  echo:  { label: 'Echo',  color: '#32cd32', role: 'TikTok Publishing' },
  lens:  { label: 'Lens',  color: '#ff8c00', role: 'Analytics & Insights' },
}

const STAT_COLORS = {
  VRL: '#ff69b4',
  SPD: '#00ff00',
  RCH: '#00bfff',
  TRU: '#ffd700',
  WIS: '#ff8c00',
  CRE: '#9370db',
}

// ─── Module State ────────────────────────────────────────────
let selectedAgent = null
let onAgentSelect = null

// Track previous pipeline count values for flash animation
const prevCounts = { pending: null, generating: null, ready: null, posted: null }

// ─── Init ────────────────────────────────────────────────────

/**
 * Initialize panels. Call once at boot.
 * @param {function(string|null): void} agentSelectCallback - Called with agent name on select, null on deselect.
 */
export function initPanels(agentSelectCallback) {
  onAgentSelect = agentSelectCallback
  buildAgentSelector()
  setupKeyboard()
}

// ─── Agent Selector ──────────────────────────────────────────

/**
 * Populate #agent-selector with one button per agent.
 */
function buildAgentSelector() {
  const container = document.getElementById('agent-selector')
  if (!container) return

  container.innerHTML = ''

  for (const [name, meta] of Object.entries(AGENT_META)) {
    const btn = document.createElement('div')
    btn.className = 'agent-btn'
    btn.dataset.agent = name

    // Portrait circle
    const portrait = document.createElement('div')
    portrait.className = 'agent-portrait'
    portrait.style.cssText = [
      'width: 32px',
      'height: 32px',
      'border-radius: 50%',
      `border: 2px solid ${meta.color}`,
      `background-image: url('/assets/pixellab/${name}/rotations/south.png')`,
      'background-size: cover',
      'background-position: center',
      `background-color: ${meta.color}22`,  // fallback tint if image missing
    ].join('; ')

    // Label
    const label = document.createElement('div')
    label.className = 'agent-btn-label'
    label.textContent = meta.label.toUpperCase()
    label.style.cssText = [
      'font-size: 9px',
      'text-transform: uppercase',
      `color: ${meta.color}`,
      'margin-top: 4px',
      'text-align: center',
      'font-family: "Courier New", monospace',
      'letter-spacing: 1px',
    ].join('; ')

    // State dot
    const dot = document.createElement('div')
    dot.className = 'agent-state-dot dot-idle'
    dot.id = `dot-${name}`
    dot.style.cssText = [
      'width: 6px',
      'height: 6px',
      'border-radius: 50%',
      'margin: 3px auto 0',
      'background: #666',
      'transition: background 0.3s ease',
    ].join('; ')

    btn.appendChild(portrait)
    btn.appendChild(label)
    btn.appendChild(dot)

    btn.addEventListener('click', () => selectAgent(name))

    container.appendChild(btn)
  }
}

// ─── Agent Selection ─────────────────────────────────────────

/**
 * Select or toggle-deselect an agent.
 * @param {string} name - Agent key (e.g. 'atlas')
 */
export function selectAgent(name) {
  // Toggle off if same agent clicked again
  if (selectedAgent === name) {
    deselectAgent()
    return
  }

  selectedAgent = name

  // Update .selected classes on all buttons
  const buttons = document.querySelectorAll('.agent-btn')
  buttons.forEach((btn) => {
    if (btn.dataset.agent === name) {
      btn.classList.add('selected')
    } else {
      btn.classList.remove('selected')
    }
  })

  if (onAgentSelect) onAgentSelect(name)
}

/**
 * Deselect the current agent and hide the stat card.
 */
export function deselectAgent() {
  selectedAgent = null

  const buttons = document.querySelectorAll('.agent-btn')
  buttons.forEach((btn) => btn.classList.remove('selected'))

  // Hide stat card
  const card = document.getElementById('agent-card')
  if (card) card.classList.remove('visible')

  if (onAgentSelect) onAgentSelect(null)
}

/**
 * Return the currently selected agent name, or null.
 * @returns {string|null}
 */
export function getSelectedAgent() {
  return selectedAgent
}

// ─── Stat Card ───────────────────────────────────────────────

/**
 * Populate and show the agent stat card.
 * @param {string} name - Agent key
 * @param {object|null} stats - Stats object from dashboard_stats row, or null
 */
export function showStatCard(name, stats) {
  const meta = AGENT_META[name]
  if (!meta) return

  const card    = document.getElementById('agent-card')
  const nameEl  = document.getElementById('card-name')
  const levelEl = document.getElementById('card-level')
  const roleEl  = document.getElementById('card-role')
  const statsEl = document.getElementById('card-stats')
  const actEl   = document.getElementById('card-activity')

  if (!card || !nameEl || !levelEl || !statsEl) return

  // Name — use title if available, otherwise meta label
  const displayName = (stats && stats.title) ? stats.title : meta.label
  nameEl.textContent = displayName
  nameEl.style.color = meta.color

  // Level
  levelEl.textContent = stats ? `Level ${stats.level} ${meta.label}` : meta.label

  // Role (element may be optional in older HTML)
  if (roleEl) {
    roleEl.textContent = meta.role
    roleEl.style.color = '#888'
  }

  // Stats bars
  if (!stats || !stats.stats || Object.keys(stats.stats).length === 0) {
    statsEl.innerHTML = '<div style="color: #555; font-size: 11px; padding: 8px 0;">No stats computed yet</div>'
  } else {
    statsEl.innerHTML = Object.entries(stats.stats).map(([key, val]) => {
      const color = STAT_COLORS[key] || '#666'
      return `
        <div class="stat-row">
          <span style="color: ${color}; min-width: 32px; font-size: 11px;">${key}</span>
          <div class="stat-bar">
            <div class="stat-fill" style="width: ${val}%; background: ${color};"></div>
          </div>
          <span style="font-size: 11px; min-width: 28px; text-align: right;">${val}</span>
        </div>
      `.trim()
    }).join('')
  }

  // Last activity line
  if (actEl) {
    if (stats && stats.last_action) {
      actEl.textContent = `Last: ${stats.last_action}`
      actEl.style.cssText = 'color: #555; font-size: 10px; margin-top: 8px; font-style: italic;'
    } else {
      actEl.textContent = ''
    }
  }

  // Make card visible
  card.classList.add('visible')
}

// ─── Pipeline Counts ─────────────────────────────────────────

/**
 * Update the pipeline status count elements with a flash animation on change.
 * @param {{ pending: number, generating: number, ready: number, posted: number }} counts
 */
export function updatePipelineCounts(counts) {
  const keys = ['pending', 'generating', 'ready', 'posted']

  for (const key of keys) {
    const el = document.getElementById(`count-${key}`)
    if (!el) continue

    const newVal = counts[key] ?? 0

    if (prevCounts[key] !== null && prevCounts[key] !== newVal) {
      // Flash scale animation on value change
      el.style.transition = 'transform 0.15s ease'
      el.style.transform = 'scale(1.3)'
      setTimeout(() => {
        el.style.transform = 'scale(1)'
        setTimeout(() => {
          el.style.transition = ''
        }, 300)
      }, 300)
    }

    el.textContent = newVal
    prevCounts[key] = newVal
  }
}

// ─── Activity Feed ───────────────────────────────────────────

/**
 * Map event_type strings to CSS tag class + display category.
 */
const EVENT_TAG_MAP = {
  workflow_run:              'tag-workflow',
  workflow_completed:        'tag-workflow',
  heartbeat_complete:        'tag-workflow',
  content_brief_created:     'tag-content',
  content_ready:             'tag-content',
  content_generation_failed: 'tag-error',
  content_posted:            'tag-posting',
  posting_failed:            'tag-error',
  outside_posting_window:    'tag-posting',
  daily_report:              'tag-analytics',
  morning_roundtable:        'tag-roundtable',
  fatal_error:               'tag-error',
  stats_computed:            'tag-analytics',
  stale_claim_reset:         'tag-workflow',
}

/**
 * Add an entry to the #activity-feed. Newest entries appear first.
 * Caps at 50 entries total.
 * @param {string} agent - Agent key
 * @param {string} message - Activity message
 * @param {Date|string|number} timestamp - Entry timestamp
 * @param {string} [eventType] - Event type string for badge display
 */
export function addFeedEntry(agent, message, timestamp, eventType) {
  const feed = document.getElementById('activity-feed')
  if (!feed) return

  const meta = AGENT_META[agent] || { label: agent, color: '#aaa' }
  const time = formatTime(timestamp)

  // Entry container — uses CSS classes from index.html
  const entry = document.createElement('div')
  entry.className = 'feed-entry'

  // Header row: agent name + event badge (left) | timestamp (right)
  const header = document.createElement('div')
  header.className = 'feed-entry-header'

  const headerLeft = document.createElement('div')
  headerLeft.className = 'feed-header-left'

  const agentSpan = document.createElement('span')
  agentSpan.className = `feed-agent-name ${agent}`
  agentSpan.textContent = meta.label

  headerLeft.appendChild(agentSpan)

  // Event type badge
  if (eventType) {
    const tagClass = EVENT_TAG_MAP[eventType] || 'tag-default'
    const badge = document.createElement('span')
    badge.className = `feed-event-tag ${tagClass}`
    badge.textContent = eventType.replace(/_/g, ' ')
    headerLeft.appendChild(badge)
  }

  const timeSpan = document.createElement('span')
  timeSpan.className = 'feed-timestamp'
  timeSpan.textContent = time

  header.appendChild(headerLeft)
  header.appendChild(timeSpan)

  // Message body
  const msgDiv = document.createElement('div')
  msgDiv.className = 'feed-message'
  msgDiv.textContent = message || ''

  entry.appendChild(header)
  entry.appendChild(msgDiv)

  // Click entry to select its agent
  entry.addEventListener('click', () => selectAgent(agent))

  // Prepend (newest first)
  feed.insertBefore(entry, feed.firstChild)

  // Cap at 50 entries
  while (feed.children.length > 50) {
    feed.removeChild(feed.lastChild)
  }
}

// ─── Agent Dot State ─────────────────────────────────────────

/**
 * Update the state indicator dot for an agent.
 * @param {string} name - Agent key
 * @param {string} state - State name: 'idle' | 'working' | 'talking' | 'celebrating' | 'frustrated' | 'walking'
 */
export function updateAgentDot(name, state) {
  const dot = document.getElementById(`dot-${name}`)
  if (!dot) return

  dot.className = `agent-state-dot ${state}`

  // Update dot background color to match state
  const dotColors = {
    idle:        '#444',
    working:     '#00ff00',
    talking:     '#00bfff',
    celebrating: '#ffd700',
    frustrated:  '#ff4444',
    walking:     '#ffaa00',
  }
  dot.style.background = dotColors[state] || dotColors.idle
}

// ─── Connection Status ───────────────────────────────────────

/**
 * Update the connection status indicator in the UI.
 * @param {boolean} connected
 */
export function setConnectionStatus(connected) {
  const indicator = document.getElementById('status-indicator')
  if (!indicator) {
    // Fall back to legacy #status-bar if #status-indicator doesn't exist
    const statusBar = document.getElementById('status-bar')
    if (statusBar) {
      statusBar.className = connected ? 'connected' : 'disconnected'
      statusBar.textContent = connected ? 'Connected to Supabase Realtime' : 'Disconnected — reconnecting...'
    }
    return
  }

  const textEl = document.getElementById('status-text')
  if (connected) {
    indicator.className = 'connected'
    if (textEl) textEl.textContent = 'Connected'
  } else {
    indicator.className = 'disconnected'
    if (textEl) textEl.textContent = 'Reconnecting...'
  }
}

// ─── Keyboard Navigation ─────────────────────────────────────

/**
 * Wire keyboard shortcuts: 1-5 select agents, Escape deselects.
 */
function setupKeyboard() {
  const agentKeys = Object.keys(AGENT_META) // ['atlas','muse','pixel','echo','lens']

  document.addEventListener('keydown', (e) => {
    // Ignore if focus is in an input/textarea
    const tag = document.activeElement?.tagName
    if (tag === 'INPUT' || tag === 'TEXTAREA') return

    if (e.key === 'Escape') {
      deselectAgent()
      return
    }

    const idx = parseInt(e.key, 10)
    if (idx >= 1 && idx <= agentKeys.length) {
      selectAgent(agentKeys[idx - 1])
    }
  })
}

// ─── Helpers ─────────────────────────────────────────────────

/**
 * Format a timestamp as "10:19 AM ET" in US Eastern time.
 * @param {Date|string|number|null|undefined} ts
 * @returns {string}
 */
function formatTime(ts) {
  try {
    const d = ts instanceof Date ? ts : new Date(ts)
    if (isNaN(d.getTime())) return '--:-- ET'
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/New_York',
    }) + ' ET'
  } catch (_) {
    return '--:-- ET'
  }
}
